import React, { useRef, useEffect } from 'react';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { Formik, Form, useFormikContext } from 'formik';
import { THEME, ThemeProvider } from '@vodacomsa/ns-react-components';

import {
  FormContainer
} from '../../../../../constants';
import { Button } from '../../../../../components/v3';
import { useTealium} from '../../../../../tealium/useTealium';
import { useLocation } from 'react-router-dom';
import { findFirstValidationError } from '../../../../../helpers/formik';
import { useModal } from '../../../../../components/modalController/useModal';
import { IncreaseLimitSuccessModal } from '../increaseLimitSuccessModal/increaseLimitSuccessModal';
import { increaseCapitalFacilityLimit } from '../../../../../api/vodaLend';
import { formatCurrency } from '../../../../../helpers';
import { Fonts } from '../../../../../constants';
import { useLoader } from '../../../../../components/Loader/useLoader';
import { getErrorStatus } from '../../../../../api/vodaLend/axiosInstance';
import { StatusCodes } from 'http-status-codes';
import { useIsMobile, capitalFacilityState } from '../../../../../recoil';
import { useRecoilValue, useRecoilState } from 'recoil';
import { formFields } from './IncreaseLimitModalConstant';
import { LimitIncreaseForm } from './LimitIncreaseForm';
import { convertCurrencyAmountToNumber } from '../../../../../helpers';
import styles from './increaseLimitModal.module.css';

const MAX_LIMIT = 5000000;
const MIN_LIMIT = 1000;

export const IncreaseLimitModal = ({ capitalFacilityId, currentLimit, onView = () => { }, onSuccess, businessId, applicationId }) => {
  const { track, events } = useTealium();

  useEffect(() => {
    onView();
  }, []);

  const validationSchema = yup.object({
    limit: yup
      .number().integer('Limit increase amount must be a whole numerical value')
      .min(MIN_LIMIT, `Limit should be greater than or equal to ${MIN_LIMIT}`)
      .max(MAX_LIMIT, `Limit should be less than or equal to ${MAX_LIMIT}`)
      .test(
        'is-less-than-limit',
        'Total amount must be lower or equal to R5m',
        function (value) {
          const total = Number(currentLimit) + Number(value);
          return total <= MAX_LIMIT;
        })
      .required('Enter a limit amount')
      .transform((value, originalValue) => {
        const trimmed = String(originalValue).trim();
        return trimmed === '' ? undefined : Number(trimmed);
      })
  });

  const formInitialValues = {
    currentLimit: formatCurrency(currentLimit),
    limit: '',
    newLimit: ''
  };

  const [openModal, closeModal] = useModal();
  const formRef = useRef();
  const formStartedRef = useRef(false);
  const location = useLocation();
  const [openLoader, closeLoader] = useLoader();
  const isMobile = useRecoilValue(useIsMobile);
  const [cfDetails,setCfDetails] = useRecoilState(capitalFacilityState({ businessId, applicationId }));

  const handleClose = () => {
    closeModal();
  };

  const handleBackClick = async () => {
    track(events.limitIncrease.formEnd('back'));
    closeModal();
  };

  const handleSubmit = async () => {
    const amount = convertCurrencyAmountToNumber(formRef.current?.values?.limit);
    track(events.limitIncrease.formEnd('submit', amount));
    closeModal();
    openLoader();
    try {
      await increaseCapitalFacilityLimit(capitalFacilityId, amount, businessId, applicationId);
      setCfDetails({ ...cfDetails, limitIncreaseEligibility: false });
      closeModal();
      openModal({
        title: '',
        children: <IncreaseLimitSuccessModal onSuccess={onSuccess} />,
        type: 'success',
        noCloseIcon: isMobile ? true : false,
        actions: [
          <Button key="increaseLimitSuccessClose" style={style.submitBtn} onClick={() => {track(events.limitIncrease.modalClose('Got it')); handleClose();}}>Got it</Button>
        ],
        onCloseModal: handleClose
      });
    } catch (error) {
      const status = getErrorStatus(error);
      closeModal();
      openModal({
        title: 'In progress',
        children: <div>{status === StatusCodes.BAD_REQUEST ? 'Current Limit Increase in Progress' : 'There was an error submitting your limit increase request.'}</div>,
        type: '',
        actions: [
          <Button key="increaseLimitSuccessClose" style={style.submitBtn} onClick={() => {track(events.limitIncrease.modalClose('Okay')); handleClose();}}>Okay</Button>
        ],
        onCloseModal: handleClose
      });
    } finally {
      closeLoader();
    }
  };

  const handleFormikTouchedErrors = (touched, errors) => {
    const errorMessage = findFirstValidationError(errors, touched);

    if (!errorMessage) {
      return;
    }

    track(events.error('form', location.pathname, errorMessage, 'Cash Flow Facility limit increase'));
  };

  const handleFormFocus = () => {
    if (formStartedRef.current)
    {
      return;
    }
    formStartedRef.current = true;
    track(events.limitIncrease.formStart());
  };

  return (
    <div style={{ marginTop: '-40px', width: '500px !important' }}>
      <ThemeProvider theme={THEME.revamp} >
        <Formik
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          validate={(values) => {
            try {
              validationSchema.validateSync(values, { abortEarly: false });
              return {};
            } catch (error) {
              return error.inner.reduce((acc, err) => {
                acc[err.path] = err.message;
                return acc;
              }, {});
            }
          }}
          validateOnBlur={true}
          validateOnMount={true}
          innerRef={formRef}
        >
          {({ values, errors, touched }) => {
            handleFormikTouchedErrors(touched, errors);
            return (
              <div style={!isMobile ? { marginTop: '-70px' } : { marginTop: '-40px' }} className={!isMobile ? styles.customFormWrapper : ''}>
                <Form style={FormContainer} onFocus={handleFormFocus}>
                  <UpdateNewLimit currentLimit={currentLimit} />
                  <LimitIncreaseForm
                    fields={formFields}
                    values={values}
                    errors={errors}
                    touched={touched}
                    requireFooter={true}
                    isMobile={isMobile}
                    showConfirmButton={true}
                    showCancelButton={true}
                    backClick={handleBackClick}
                    nextClick={handleSubmit}
                  />
                </Form>
              </div>);
          }}
        </Formik>
      </ThemeProvider>
    </div>
  );
};

IncreaseLimitModal.propTypes = {
  capitalFacilityId: PropTypes.number.isRequired,
  currentLimit: PropTypes.number.isRequired,
  onView: PropTypes.func,
  onSuccess: PropTypes.func,
  onClick: PropTypes.func,
  businessId: PropTypes.string,
  applicationId: PropTypes.string
};

const style = {
  submitBtn: {
    font: Fonts.Roboto,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '220px!important',
    maxWidth: '352px',
    height: '50px',
    minHeight: '50px',
    fontSize: '16px',
    borderRadius: '50px',
    backgroundColor: '#007C92'
  }
};

const UpdateNewLimit = ({ currentLimit }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    const parsedLimit = Number(values.limit);
    if (!isNaN(parsedLimit)) {
      setFieldValue('newLimit', formatCurrency(currentLimit + parsedLimit));
    } else {
      setFieldValue('newLimit', '');
    }
  }, [values.limit, currentLimit, setFieldValue]);

  return null;
};

UpdateNewLimit.propTypes = {
  currentLimit: PropTypes.number.isRequired
};
